import { VgEmploymentState } from '@interfaces';
import { createSelector } from '@ngrx/store';
import {
  selectHasAvgCostHoldings,
  selectHasMinTaxHoldings,
  selectMultiITFromSelectedAccount,
} from '@store/internal-transfers/selectors/multi-internal-transfer.selectors';
import {
  selectHasCompletedAccounts,
  selectNewAccountRecipients,
} from '@store/internal-transfers/selectors/open-account/open-account.selectors';
import { getCostBasis, getInternalTransferState } from '@store/reducers';
import { InternalTransferState } from '@store/reducers/internal-transfer/internal-transfer.reducer';
import { selectItMultiReceivingToggle } from '@store/selectors/feature/feature.selectors';
import {
  selectEmploymentMfeDataByOwnerIndex,
  selectIsEditingProfile,
} from '@store/selectors/open-account/open-account.selectors';

import { nullState } from '../common/router.selectors';
import {
  getDivorceAmountsNextPage,
  getDivorceCostBasisElectionNextPage,
  getDivorceDocUploadNextPage,
  getDivorceFromToNextPage,
  getDivorceMultiReviewNextPage,
  getDivorceReviewHoldingsNextPage,
  getDivorceReviewNextPage,
  getDivorceTermsNextPage,
  getDivorceTransferDetailsNextPage,
  getInternalTransferAmountsNextPage,
  getInternalTransferContactInfoNextPage,
  getInternalTransferCostBasisElectionMultiNextPage,
  getInternalTransferCostBasisElectionNextPage,
  getInternalTransferDetailsNextPage,
  getInternalTransferEmploymentNextPage,
  getInternalTransferFromToNextPage,
  getInternalTransferHoldingsReviewNextPage,
  getInternalTransferIncomeNetWorthNextPage,
  getInternalTransferInvestObjNextPage,
  getInternalTransferLetterUploadNextPage,
  getInternalTransferMultiReviewNextPage,
  getInternalTransferNewAccountReviewNextPage,
  getInternalTransferOverviewNextPage,
  getInternalTransferPersonalInfoNextPage,
  getInternalTransferReviewNextPage,
  getInternalTransferSettlementFundNextPage,
  getInternalTransferTermsExistingNextPage,
  getInternalTransferTermsNewNextPage,
  getInternalTransferTriageMultiNextPage,
  getInternalTransferTriageNextPage,
} from './internal-transfer-router.helpers';

export const selectInternalTransferOverviewNextPage = createSelector(
  selectItMultiReceivingToggle,
  (itMultiReceivingToggle: boolean) => getInternalTransferOverviewNextPage(itMultiReceivingToggle),
);

export const selectInternalTransferTriageNextPage = createSelector(nullState, () =>
  getInternalTransferTriageNextPage(),
);

export const selectInternalTransferTriageMultiNextPage = createSelector(nullState, () =>
  getInternalTransferTriageMultiNextPage(),
);

export const selectInternalTransferDetailsNextPage = createSelector(
  getInternalTransferState,
  getCostBasis,
  (internalTransfer: InternalTransferState, costBasis) =>
    getInternalTransferDetailsNextPage(internalTransfer, costBasis),
);

export const selectInternalTransferHoldingsReviewNextPage = createSelector(
  getInternalTransferState,
  (internalTransfer: InternalTransferState) =>
    getInternalTransferHoldingsReviewNextPage(internalTransfer),
);

export const selectInternalTransferPersonalInfoNextPage = createSelector(nullState, () =>
  getInternalTransferPersonalInfoNextPage(),
);
export const selectInternalTransferContactInfoNextPage = createSelector(
  selectItMultiReceivingToggle,
  (itMultiReceivingToggle) => getInternalTransferContactInfoNextPage(itMultiReceivingToggle),
);
export const selectInternalTransferSettlementFundNextPage = createSelector(
  selectHasCompletedAccounts,
  selectItMultiReceivingToggle,
  (isNotFirst, itMultiReceivingToggle) =>
    getInternalTransferSettlementFundNextPage(isNotFirst, itMultiReceivingToggle),
);
export const selectInternalTransferEmploymentNextPage = createSelector(
  selectEmploymentMfeDataByOwnerIndex(0),
  (employment: Partial<VgEmploymentState> | undefined) =>
    getInternalTransferEmploymentNextPage(employment),
);
export const selectInternalTransferLetterUploadNextPage = createSelector(nullState, () =>
  getInternalTransferLetterUploadNextPage(),
);
export const selectInternalTransferIncomeNetWorthNextPage = createSelector(
  selectItMultiReceivingToggle,
  selectIsEditingProfile,
  (itMultiReceivingToggle, isEditingProfile) =>
    getInternalTransferIncomeNetWorthNextPage(itMultiReceivingToggle, isEditingProfile),
);
export const selectInternalTransferInvestObjNextPage = createSelector(
  selectItMultiReceivingToggle,
  (itMultiReceivingToggle) => getInternalTransferInvestObjNextPage(itMultiReceivingToggle),
);

export const selectInternalTransferNewAccountReviewNextPage = createSelector(nullState, () =>
  getInternalTransferNewAccountReviewNextPage(),
);

export const selectInternalTransferReviewNextPage = createSelector(
  getInternalTransferState,
  (internalTransfer: InternalTransferState) => getInternalTransferReviewNextPage(internalTransfer),
);

export const selectInternalTransferCostBasisElectionNextPage = createSelector(nullState, () =>
  getInternalTransferCostBasisElectionNextPage(),
);

export const selectInternalTransferTermsExistingNextPage = createSelector(
  getInternalTransferState,
  (internalTransfer: InternalTransferState) =>
    getInternalTransferTermsExistingNextPage(internalTransfer),
);

export const selectInternalTransferTermsNewNextPage = createSelector(nullState, () =>
  getInternalTransferTermsNewNextPage(),
);

export const selectDivorceDocUploadNextPage = createSelector(
  selectItMultiReceivingToggle,
  (itMultiReceivingToggle) => getDivorceDocUploadNextPage(itMultiReceivingToggle),
);

export const selectDivorceTransferDetailsNextPage = createSelector(nullState, () =>
  getDivorceTransferDetailsNextPage(),
);

export const selectDivorceReviewHoldingsNextPage = createSelector(nullState, () =>
  getDivorceReviewHoldingsNextPage(),
);

export const selectDivorceReviewNextPage = createSelector(nullState, () =>
  getDivorceReviewNextPage(),
);

export const selectDivorceTermsNextPage = createSelector(nullState, () =>
  getDivorceTermsNextPage(),
);

export const selectDivorceFromToNextPage = createSelector(nullState, () =>
  getDivorceFromToNextPage(),
);

export const selectDivorceAmountsNextPage = createSelector(
  selectHasAvgCostHoldings,
  selectHasMinTaxHoldings,
  selectMultiITFromSelectedAccount,
  (hasAvgCostHoldings, hasMinTaxHoldings, selectedAccount) =>
    getDivorceAmountsNextPage(
      hasAvgCostHoldings,
      hasMinTaxHoldings,
      selectedAccount?.accountData?.managed ?? false,
    ),
);

export const selectDivorceCostBasisElectionNextPage = createSelector(nullState, () =>
  getDivorceCostBasisElectionNextPage(),
);

export const selectDivorceMultiReviewNextPage = createSelector(nullState, () =>
  getDivorceMultiReviewNextPage(),
);

// Multi-receiving flow selectors
export const selectInternalTransferFromToNextPage = createSelector(
  selectNewAccountRecipients,
  selectItMultiReceivingToggle,
  (newAccounts, itMultiReceivingToggle) =>
    getInternalTransferFromToNextPage((newAccounts?.length ?? 0) > 0, itMultiReceivingToggle),
);

export const selectInternalTransferAmountsNextPage = createSelector(
  selectHasAvgCostHoldings,
  selectHasMinTaxHoldings,
  selectMultiITFromSelectedAccount,
  (hasAvgCostHoldings, hasMinTaxHoldings, selectedAccount) =>
    getInternalTransferAmountsNextPage(
      hasAvgCostHoldings,
      hasMinTaxHoldings,
      selectedAccount?.accountData?.managed ?? false,
    ),
);

export const selectInternalTransferCostBasisElectionMultiNextPage = createSelector(nullState, () =>
  getInternalTransferCostBasisElectionMultiNextPage(),
);

export const selectInternalTransferMultiReviewNextPage = createSelector(
  selectNewAccountRecipients,
  (newAccounts) => getInternalTransferMultiReviewNextPage((newAccounts?.length ?? 0) > 0),
);
